import { makeStyles } from "@mui/styles";

/* The app's palette: teal borders and links, orange actions, green / red states. */
export const tokens = {
    teal: '#14A4C2',
    tealDark: '#317787',
    tealSoft: '#e8f6f9',
    tealLine: '#cfe9f0',
    orange: '#FCB364',
    orangeSoft: '#fff2e4',
    green: '#0f8a60',
    greenSoft: '#e3f7ef',
    red: '#c62828',
    redSoft: '#fdecea',
    amber: '#9a5b00',
    amberSoft: '#fff6e0',
    grey: '#6b7a80',
    greySoft: '#f1f4f5',
    text: '#1f2d33',
};

const tones = {
    changed: { color: tokens.tealDark, background: tokens.tealSoft },
    saved: { color: tokens.green, background: tokens.greenSoft },
    neutral: { color: tokens.grey, background: tokens.greySoft },
    warning: { color: tokens.amber, background: tokens.amberSoft },
    error: { color: tokens.red, background: tokens.redSoft },
};

const useStyles = makeStyles({
    page: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        overflow: 'auto',
        color: tokens.text,
    },

    /* Intro */
    intro: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '10px 14px',
        borderRadius: 8,
        backgroundColor: tokens.tealSoft,
        color: tokens.tealDark,
        fontSize: 13,
        lineHeight: 1.5,
        '& svg': { fontSize: 18, marginTop: 1, flexShrink: 0 },
        '& p': { margin: 0 },
    },

    /* Invoice input panel */
    panel: {
        flexShrink: 0,
        border: `1px solid ${tokens.tealLine}`,
        borderRadius: 10,
        padding: '14px 16px 16px',
        backgroundColor: '#fff',
    },
    panelHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        marginBottom: 8,
    },
    panelTitle: {
        fontSize: 14,
        fontWeight: 600,
        margin: 0,
    },
    panelMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontSize: 13,
        color: tokens.grey,
    },
    textButton: {
        border: 0,
        padding: 0,
        background: 'none',
        font: 'inherit',
        fontWeight: 600,
        color: tokens.tealDark,
        cursor: 'pointer',
        '&:disabled': { color: '#b9c4c7', cursor: 'default' },
        '&:hover:not(:disabled)': { textDecoration: 'underline' },
    },
    chipInput: {
        '& .MuiOutlinedInput-root': {
            minHeight: 48,
            maxHeight: 140,
            overflowY: 'auto',
            alignItems: 'flex-start',
            padding: '6px 8px !important',
            borderRadius: 8,
            backgroundColor: '#fbfdfd',
            '& fieldset': { borderColor: tokens.tealLine },
            '&:hover fieldset': { borderColor: tokens.teal },
            '&.Mui-focused fieldset': { borderColor: tokens.teal, borderWidth: 1, boxShadow: '0 0 0 3px rgba(20, 164, 194, 0.12)' },
            '&.Mui-error fieldset': { borderColor: tokens.red },
        },
        '& .MuiAutocomplete-input': { minWidth: '220px !important', fontSize: 14, padding: '6px 4px !important' },
    },
    chip: {
        height: '26px !important',
        margin: '3px !important',
        borderRadius: '6px !important',
        backgroundColor: `${tokens.tealSoft} !important`,
        color: `${tokens.tealDark} !important`,
        fontWeight: 500,
        fontSize: '13px !important',
        '& .MuiChip-deleteIcon': { color: `${tokens.tealDark} !important`, opacity: 0.6, fontSize: 16, '&:hover': { opacity: 1 } },
    },
    panelFooter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
        marginTop: 12,
    },
    hint: {
        fontSize: 12,
        color: tokens.grey,
        margin: 0,
        '&$hintError': { color: tokens.red },
    },
    hintError: {},

    /* Results header */
    resultsHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
    },
    resultsTitle: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 10,
        margin: 0,
        fontSize: 16,
        fontWeight: 600,
    },
    resultsTime: { fontSize: 12, fontWeight: 400, color: tokens.grey },
    applyArea: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        gap: 12,
    },
    buttonDisabled: {
        opacity: 0.5,
        boxShadow: 'none !important',
        cursor: 'not-allowed !important',
        pointerEvents: 'auto !important',
    },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 8px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.5,
        textTransform: 'uppercase',
        lineHeight: 1.3,
    },
    tone_changed: tones.changed,
    tone_saved: tones.saved,
    tone_neutral: tones.neutral,
    tone_warning: tones.warning,
    tone_error: tones.error,

    staleNotice: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        borderRadius: 8,
        fontSize: 13,
        color: tokens.amber,
        backgroundColor: tokens.amberSoft,
        '& svg': { fontSize: 18 },
    },

    /* Summary tiles */
    tiles: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
    },
    tile: {
        minWidth: 118,
        padding: '10px 14px',
        borderRadius: 8,
        border: `1px solid ${tokens.tealLine}`,
        backgroundColor: '#fff',
        textAlign: 'left',
        font: 'inherit',
        color: tokens.text,
        cursor: 'pointer',
        transition: 'border-color .2s, box-shadow .2s, background-color .2s',
        '&:hover:not(:disabled)': { borderColor: tokens.teal },
        '&:disabled': { cursor: 'default', opacity: 0.55 },
        '&$tileActive': { borderColor: tokens.teal, backgroundColor: tokens.tealSoft, boxShadow: `inset 0 0 0 1px ${tokens.teal}` },
        '&:focus-visible': { outline: `2px solid ${tokens.teal}`, outlineOffset: 2 },
    },
    tileActive: {},
    tileStatic: {
        cursor: 'default',
        backgroundColor: tokens.greySoft,
        borderColor: 'transparent',
        '&:hover': { borderColor: 'transparent !important' },
    },
    tileLabel: { display: 'block', fontSize: 12, color: tokens.grey, marginBottom: 2 },
    tileValue: { display: 'block', fontSize: 20, fontWeight: 600, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' },
    tileDot: { display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginRight: 6, verticalAlign: 'middle' },

    /* Results table */
    tableWrap: {
        flex: '1 1 auto',
        minHeight: 240,
        border: `1px solid ${tokens.tealLine}`,
        borderRadius: 10,
        overflow: 'hidden',
        display: 'flex',
        // The card forces every TableContainer to a fixed height with a permanent scrollbar; this table sizes to the space left instead.
        '& .MuiTableContainer-root': { height: 'auto !important', flex: 1, overflow: 'auto !important', paddingRight: '0 !important' },
        '& th.MuiTableCell-root.MuiTableCell-head': {
            backgroundColor: '#f6fafb !important',
            borderBottom: `1px solid ${tokens.tealLine} !important`,
            color: tokens.grey,
            fontSize: 12,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            padding: '10px 12px !important',
            whiteSpace: 'nowrap',
        },
        '& td.MuiTableCell-root': { padding: '10px 12px !important', fontSize: 13, verticalAlign: 'top', borderBottom: `1px solid ${tokens.greySoft}` },
    },
    row: {
        '&$rowClickable': { cursor: 'pointer' },
        '&$rowClickable:hover td': { backgroundColor: '#f7fbfc' },
        '&$rowOpen td': { backgroundColor: '#f7fbfc', borderBottomColor: 'transparent' },
    },
    rowClickable: {},
    rowOpen: {},
    num: { textAlign: 'right !important', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
    serial: { fontWeight: 600, whiteSpace: 'nowrap' },
    subText: { fontSize: 11, fontWeight: 400, color: tokens.grey, marginTop: 2 },
    selfBillNote: { marginBottom: '10px !important', color: `${tokens.tealDark} !important` },
    expandButton: {
        padding: '2px !important',
        color: `${tokens.tealDark} !important`,
        '& svg': { fontSize: 20 },
    },
    pill: {
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        lineHeight: 1.4,
    },
    statusCell: {
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
    },
    warnFlag: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        marginLeft: 8,
        fontSize: 12,
        fontWeight: 600,
        color: tokens.amber,
        verticalAlign: 'middle',
        '& svg': { fontSize: 16 },
    },
    moneyNew: { display: 'block', fontWeight: 600 },
    moneyOld: { display: 'block', fontSize: 11, color: tokens.grey, marginTop: 2 },
    moneyStrike: { textDecoration: 'line-through' },
    delta: { marginLeft: 6, fontWeight: 600 },

    /* Expanded row */
    details: {
        padding: '4px 12px 16px 48px !important',
        backgroundColor: '#f7fbfc',
    },
    callout: {
        display: 'flex',
        gap: 8,
        padding: '8px 12px',
        borderRadius: 8,
        fontSize: 13,
        marginBottom: 10,
        '& svg': { fontSize: 18, flexShrink: 0, marginTop: 1 },
        '& ul': { margin: 0, paddingLeft: 16 },
        '& li + li': { marginTop: 2 },
    },
    detailsTitle: {
        margin: '4px 0 6px',
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        color: tokens.grey,
    },
    linesTable: {
        backgroundColor: '#fff',
        borderRadius: 8,
        border: `1px solid ${tokens.tealLine}`,
        overflow: 'hidden',
        '& th.MuiTableCell-root.MuiTableCell-head': { backgroundColor: '#fff !important', fontSize: 11 },
        '& td.MuiTableCell-root': { verticalAlign: 'top' },
        '& tr:last-child td': { borderBottom: 0 },
    },
    note: { fontSize: 13, color: tokens.grey, margin: 0 },

    /* Empty and loading states */
    placeholder: {
        flex: '1 1 auto',
        minHeight: 220,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 20,
        border: `1px dashed ${tokens.tealLine}`,
        borderRadius: 10,
        padding: 24,
        textAlign: 'center',
        color: tokens.grey,
    },
    steps: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 28,
        margin: 0,
        padding: 0,
        listStyle: 'none',
    },
    step: {
        width: 180,
        fontSize: 13,
        lineHeight: 1.45,
        '& b': { display: 'block', color: tokens.text, fontSize: 14, marginBottom: 2 },
    },
    stepNumber: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        borderRadius: '50%',
        marginBottom: 8,
        fontWeight: 700,
        color: tokens.tealDark,
        backgroundColor: tokens.tealSoft,
        border: `1px solid ${tokens.tealLine}`,
    },
    spinner: {
        width: 36,
        height: 36,
        borderRadius: '50%',
        border: `3px solid ${tokens.tealSoft}`,
        borderTopColor: tokens.teal,
        animation: '$spin 0.9s linear infinite',
    },
    '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
});

export default useStyles;
